import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "../context/AdminAuthContext";
import {
  House, Users, ArrowsDownUp, Briefcase, ShieldCheck, ClockCounterClockwise, SignOut,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import AdminOverview from "../components/admin/AdminOverview";
import AdminTraders from "../components/admin/AdminTraders";
import AdminWithdrawals from "../components/admin/AdminWithdrawals";
import AdminProp from "../components/admin/AdminProp";
import AdminAdmins from "../components/admin/AdminAdmins";
import AdminAudit from "../components/admin/AdminAudit";

export default function AdminPanel() {
  const { admin, logout } = useAdminAuth();
  const [section, setSection] = useState("overview");

  if (admin === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0E17] text-[#6B7280]">Загрузка…</div>;
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const NAV = [
    { key: "overview", label: "Обзор", icon: House },
    { key: "traders", label: "Трейдеры", icon: Users },
    { key: "withdrawals", label: "Выводы", icon: ArrowsDownUp },
    { key: "prop", label: "Prop-заявки", icon: Briefcase },
    ...(admin.super ? [{ key: "admins", label: "Администраторы", icon: ShieldCheck }] : []),
    { key: "audit", label: "Журнал", icon: ClockCounterClockwise },
  ];

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white flex flex-col lg:flex-row" data-testid="admin-panel">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-[#1F2937] bg-[#0B0F19]">
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-[#1F2937]">
          <div className="w-9 h-9 rounded-lg bg-[#E3B341]/15 ring-1 ring-inset ring-[#E3B341]/30 flex items-center justify-center">
            <ShieldCheck size={18} weight="fill" className="text-[#E3B341]" />
          </div>
          <div>
            <p className="text-[9px] tracking-[0.2em] text-[#6B7280] uppercase">Arena</p>
            <p className="text-sm font-semibold">Admin</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setSection(n.key)}
              data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${section === n.key ? "bg-[#E3B341]/10 text-[#E3B341] ring-1 ring-inset ring-[#E3B341]/20" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"}`}>
              <n.icon size={17} weight={section === n.key ? "fill" : "regular"} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1F2937]">
          <p className="text-[11px] text-[#6B7280] px-2 mb-2 truncate">{admin.email}</p>
          <Button variant="ghost" onClick={logout} className="w-full justify-start text-[#F6465D] hover:bg-[#F6465D]/10" data-testid="admin-logout">
            <SignOut size={16} className="mr-2" /> Выйти
          </Button>
        </div>
      </aside>

      {/* Mobile top bar + tabs */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1F2937] bg-[#0B0F19]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="fill" className="text-[#E3B341]" />
            <span className="text-sm font-semibold">Arena Admin</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-[#F6465D]" data-testid="admin-logout-mobile">
            <SignOut size={16} />
          </Button>
        </div>
        <div className="flex gap-1 p-2 overflow-x-auto border-b border-[#1F2937] bg-[#0B0F19]">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setSection(n.key)}
              data-testid={`nav-mobile-${n.key}`}
              className={`shrink-0 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${section === n.key ? "bg-[#E3B341]/10 text-[#E3B341]" : "text-[#9CA3AF]"}`}>
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto max-w-6xl w-full mx-auto">
        {section === "overview" && <AdminOverview />}
        {section === "traders" && <AdminTraders />}
        {section === "withdrawals" && <AdminWithdrawals />}
        {section === "prop" && <AdminProp />}
        {section === "admins" && admin.super && <AdminAdmins me={admin} />}
        {section === "audit" && <AdminAudit />}
      </main>
    </div>
  );
}
